export interface Dashboard {
  pendingClaims: number;
  claimedToday: number;
  expiringSoon: number;
  exceptionalOrders: number;
  totalOrders: number;
}

export interface AdminOrder {
  id: string;
  platform: string;
  sourceOrderId: string;
  knowledgeBaseId: string;
  knowledgeBaseName: string;
  status: "created" | "paid" | "claim_issued" | "claimed" | "refunded" | "cancelled";
  claimStatus: string;
  paidAt: string | null;
  claimExpiresAt: string | null;
  amountMinor: number;
  currency: "CNY";
  createdAt: string;
  updatedAt: string;
}

export interface ClaimTokenIssue {
  orderId: string;
  sourceOrderId: string;
  knowledgeBaseId: string;
  knowledgeBaseName: string;
  humanCode: string;
  qrToken: string;
  claimUrl: string;
  expiresAt: string;
  issuedAt: string;
  displayOnce: true;
}

export interface AnalyticsDashboard {
  range: { days: number; startDate: string; timezone: "Asia/Shanghai" };
  activeInstallations: number;
  totals: Record<string, number>;
  series: Array<{ date: string; eventName: string; knowledgeBaseId: string | null; value: number }>;
  topContent: Array<{ contentId: string; opens: number }>;
  diagnosticCounts: Record<string, number>;
  privacy: {
    retentionDays: number;
    containsPhone: false;
    containsSearchText: false;
    tracking: false;
  };
}

export interface HealthCheck {
  component: string;
  status: "healthy" | "degraded" | "failed";
  latencyMs: number | null;
  details: Record<string, unknown> | null;
  checkedAt: string;
}

export interface OperationsDashboard {
  environment: "dev" | "staging" | "production";
  domain: "slofst.com";
  checks: HealthCheck[];
  queue: { backlogCount?: number; backlogBytes?: number; oldestMessageAt?: string | null; available?: false };
  push: {
    jobs: Record<string, number>;
    registeredTokens: number;
    activeTokens: number;
    failingTokens: number;
    recentFailures: Array<Record<string, unknown>>;
  };
  analytics: { latestReceivedAt: string | null; retentionDays: number };
  audit: { appendOnly: true; entries: number };
}

export interface PushJob {
  id: string;
  kind: "daily_digest" | "breaking" | "subscription_expiring" | "security" | "support" | "test";
  knowledge_base_id: string | null;
  content_id: string | null;
  title: string;
  body: string;
  route: string | null;
  audience: "all" | "knowledge_base" | "test_device";
  target_device_id: string | null;
  scheduled_at: string;
  status: "scheduled" | "queued" | "processing" | "completed" | "partial" | "failed" | "cancelled";
  recipient_count: number;
  success_count: number;
  failure_count: number;
  created_at: string;
  completed_at: string | null;
}

export interface AdminPushInput {
  kind: PushJob["kind"];
  title: string;
  body: string;
  audience: PushJob["audience"];
  knowledgeBaseId?: string | undefined;
  targetDeviceId?: string | undefined;
  contentId?: string | undefined;
  route?: string | undefined;
  scheduledAt?: string | undefined;
  reason: string;
}

export interface ApiErrorPayload {
  code: string;
  message: string;
  requestId: string;
}

export class AdminApiError extends Error {
  readonly code: string;
  readonly requestId: string;
  constructor(payload: ApiErrorPayload) {
    super(payload.message);
    this.name = "AdminApiError";
    this.code = payload.code;
    this.requestId = payload.requestId;
  }
}

const configuredBase = import.meta.env.VITE_API_BASE_URL as string | undefined;
const resolvedBase = configuredBase
  ?? (import.meta.env.DEV ? "http://127.0.0.1:8791/v1" : undefined);
if (!resolvedBase) {
  throw new Error(
    "缺少 VITE_API_BASE_URL：非开发环境构建必须显式配置 API 地址，禁止静默回退 staging。",
  );
}
const apiBase = resolvedBase.replace(/\/$/u, "");

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (import.meta.env.DEV) headers.set("x-dev-admin-email", "phase1-admin@slofst.com");
  if (options.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  if (options.method && options.method !== "GET") headers.set("idempotency-key", crypto.randomUUID());
  const response = await fetch(`${apiBase}${path}`, { ...options, headers, credentials: "include" });
  const text = await response.text();
  const payload = text ? JSON.parse(text) as T | ApiErrorPayload : null;
  if (!response.ok) throw new AdminApiError(payload as ApiErrorPayload);
  return payload as T;
}

export async function loadDashboard(): Promise<Dashboard> {
  return request("/admin/dashboard");
}

export async function loadOrders(): Promise<AdminOrder[]> {
  const payload = await request<{ items: AdminOrder[] }>("/admin/orders?limit=100");
  return payload.items;
}

export async function loadAnalytics(): Promise<AnalyticsDashboard> {
  return request("/admin/analytics?days=14");
}

export async function loadOperations(): Promise<OperationsDashboard> {
  return request("/admin/operations");
}

export async function loadPushJobs(): Promise<PushJob[]> {
  const payload = await request<{ items: PushJob[] }>("/admin/push?limit=20");
  return payload.items;
}

export async function createPush(input: AdminPushInput): Promise<{
  id: string;
  status: "scheduled" | "queued";
  scheduledAt: string;
  audience: PushJob["audience"];
}> {
  return request("/admin/push", { method: "POST", body: JSON.stringify(input) });
}

export async function createOrder(input: {
  sourceOrderId: string;
  phone?: string;
}): Promise<AdminOrder> {
  return request("/admin/orders", {
    method: "POST",
    body: JSON.stringify({
      platform: "xiaohongshu",
      sourceOrderId: input.sourceOrderId,
      knowledgeBaseId: "ai-tools-efficiency",
      ...(input.phone ? { phone: input.phone.startsWith("+86") ? input.phone : `+86${input.phone}` } : {}),
      amountMinor: 6600,
    }),
  });
}

export async function issueClaimToken(orderId: string): Promise<ClaimTokenIssue> {
  return request(`/admin/claim-tokens/${orderId}/issue`, {
    method: "POST",
    body: JSON.stringify({ expiresInDays: 30 }),
  });
}

export async function importOrders(file: File): Promise<{
  totalRows: number;
  acceptedRows: number;
  rejectedRows: number;
}> {
  return request("/admin/orders/import", {
    method: "POST",
    headers: { "content-type": "text/csv; charset=utf-8", "x-filename": file.name },
    body: await file.text(),
  });
}
